export const LABS_VECTOR_PRODUCT_TYPES = {
  KEYRING: 'keyring',
  ACRYLIC_STAND: 'acrylic_stand',
};

/**
 * 메이플 스토리 캐릭터 아바타 벡터 상품 타입에 따른 이미지 스케일 환산
 *
 * 메이플 캐릭터 아바타 기본 크기: 46px x 72px
 *
 * 300 DPI 기준 세로 크기 환산 배율표:
 * - 4cm = 472px = 6.55 scale up
 * - 5cm = 590px = 8.19 scale up
 * - 6cm = 709px = 9.84 scale up
 * - 7cm = 827px = 11.48 scale up
 * - 8cm = 945px = 13.12 scale up
 * - 9cm = 1063px = 14.76 scale up
 */

const MAX_SIZE_MARGIN = 8;

export const KEYRING_CHARM_OPTIONS = {
  DPI: 300,
  RING_SIZE_MM: {
    RADIUS: {
      INNER: 1.5,
      OUTER: 4.5,
    },
  },
};

export const VECTOR_PRODUCT_SIZE = {
  [LABS_VECTOR_PRODUCT_TYPES.KEYRING]: {
    FREE: 'FREE',
  },
  [LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND]: {
    SMALL: 'SMALL',
    LARGE: 'LARGE',
  },
};

export const VECTOR_PRODUCT_OPTIONS = {
  [LABS_VECTOR_PRODUCT_TYPES.KEYRING]: {
    TYPE: LABS_VECTOR_PRODUCT_TYPES.KEYRING,
    SIZE: {
      [VECTOR_PRODUCT_SIZE[LABS_VECTOR_PRODUCT_TYPES.KEYRING].FREE]: {
        NAME: VECTOR_PRODUCT_SIZE[LABS_VECTOR_PRODUCT_TYPES.KEYRING].FREE,
        TARGET_SCALE: 6.2, // ref to 300px x 300px -> 1920px x 1920px (아바타 크기가 38 mm 되는 사이즈)
        MAX_SIZE_MM: {
          WIDTH: 100 - MAX_SIZE_MARGIN,
          HEIGHT: 100 - MAX_SIZE_MARGIN,
        },
        RING_SIZE_MM: {
          RADIUS: {
            INNER: 1.5,
            OUTER: 4.5,
          },
        },
        CUTLINE_YOYO: 3,
        BINARIZE_ALPHA_THRESHOLD: 0.03,
        DPI: 300,
        CUTLINE_MM: {
          OFFSET: 0.5,
          SMOOTHNESS: 6.6,
        },
      },
    },
  },
  [LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND]: {
    TYPE: LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND,
    SIZE: {
      [VECTOR_PRODUCT_SIZE[LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND].SMALL]: {
        NAME: VECTOR_PRODUCT_SIZE[LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND].SMALL,
        TARGET_SCALE: 8.2, // 이미지 캔버스를 이만큼 scale up 진행. svg 칼선 offset 은 양의 방향으로
        MAX_SIZE_MM: {
          WIDTH: 140 - MAX_SIZE_MARGIN,
          HEIGHT: 140 - MAX_SIZE_MARGIN,
        },
        STAND_SIZE_MM: {
          GROUND: {
            WIDTH: 21,
            HEIGHT: 0,
          },
          NOTCH: {
            WIDTH: 15,
            HEIGHT: 3,
            ROUND: 0.5,
          },
        },
        CUTLINE_YOYO: 3,
        BINARIZE_ALPHA_THRESHOLD: 0.03,
        DPI: 300,
        CUTLINE_MM: {
          OFFSET: 0.5,
          SMOOTHNESS: 6.6,
        },
      },
      [VECTOR_PRODUCT_SIZE[LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND].LARGE]: {
        NAME: VECTOR_PRODUCT_SIZE[LABS_VECTOR_PRODUCT_TYPES.ACRYLIC_STAND].LARGE,
        TARGET_SCALE: 11.7, // ref to 300px x 300px -> 3570px x 3570px (아바타 크기가 약 70 mm 되는 사이즈)
        MAX_SIZE_MM: {
          WIDTH: 200 - MAX_SIZE_MARGIN,
          HEIGHT: 200 - MAX_SIZE_MARGIN,
        },
        STAND_SIZE_MM: {
          GROUND: {
            WIDTH: 30,
            HEIGHT: 0,
          },
          NOTCH: {
            WIDTH: 20,
            HEIGHT: 3,
            ROUND: 0.5,
          },
        },
        CUTLINE_YOYO: 3,
        BINARIZE_ALPHA_THRESHOLD: 0.03,
        DPI: 300,
        CUTLINE_MM: {
          OFFSET: 0.5,
          SMOOTHNESS: 6.5,
        },
      },
    },
  },
};

export const ERROR_MESSAGES = {
  // Common validation errors
  NAME_REQUIRED: 'NAME is required and must be a string',
  MAX_SIZE_REQUIRED: 'MAX_SIZE_MM is required and must be an object',
  MAX_SIZE_WIDTH_INVALID: 'MAX_SIZE_MM.WIDTH must be a positive number',
  MAX_SIZE_HEIGHT_INVALID: 'MAX_SIZE_MM.HEIGHT must be a positive number',

  // Input validation errors
  IMAGE_URL_REQUIRED: 'Image URL is required and must be a string',
  INVALID_IMAGE_URL: 'Invalid image URL format',
  INVALID_PRODUCT_TYPE: 'Invalid product type',
  INVALID_PRODUCT_SIZE: 'Invalid product size',
  INVALID_CUTLINE_YOYO: 'cutlineYoyo must be a non-negative integer',
  INVALID_DEBUG_TYPE: 'debug must be a boolean value',

  // ScaleVO validation
  INVALID_PRODUCT_TARGET_SCALE: 'Target scale must be a positive number',

  // DPI validation
  INVALID_DPI: 'DPI must be a positive integer',

  // Cutline validation
  INVALID_CUTLINE_OPTIONS: 'Cutline options must be an object',
  INVALID_CUTLINE_OFFSET: 'Cutline offset must be a non-negative number',
  INVALID_CUTLINE_SMOOTHNESS: 'Cutline smoothness must be a non-negative number',

  // Alpha threshold validation
  INVALID_ALPHA_THRESHOLD: 'Alpha threshold must be a number between 0 and 1',

  // Padding validation
  INVALID_PADDING_OPTIONS: 'Padding options must be an object',
  INVALID_PADDING_KEYS: 'Invalid padding keys. Only TOP, BOTTOM, RIGHT, LEFT are allowed',
  INVALID_TOP_PADDING: 'Top padding must be a non-negative number',
  INVALID_BOTTOM_PADDING: 'Bottom padding must be a non-negative number',
  INVALID_RIGHT_PADDING: 'Right padding must be a non-negative number',
  INVALID_LEFT_PADDING: 'Left padding must be a non-negative number',

  // Acrylic stand specific validation
  STAND_SIZE_REQUIRED: 'Stand size options are required',
  GROUND_SIZE_REQUIRED: 'Ground size options are required',
  INVALID_GROUND_WIDTH: 'Ground width must be a positive number',
  INVALID_GROUND_HEIGHT: 'Ground height must be a non-negative number',
  NOTCH_SIZE_REQUIRED: 'Notch size options are required',
  INVALID_NOTCH_WIDTH: 'Notch width must be a positive number',
  INVALID_NOTCH_HEIGHT: 'Notch height must be at least 1',
  INVALID_NOTCH_ROUND: 'Notch round must be a positive number',
  INVALID_GROUND_NOTCH_RELATION: 'Ground width must be greater than notch width',

  // Keyring specific validation
  RING_SIZE_REQUIRED: 'Ring size options are required',
  RING_RADIUS_REQUIRED: 'Ring radius options are required',
  INVALID_INNER_RADIUS: 'Inner radius must be a positive number',
  INVALID_OUTER_RADIUS: 'Outer radius must be a positive number',
  INVALID_RADIUS_RELATION: 'Outer radius must be at least 1mm larger than inner radius',

  // Charm specific validation
  INVALID_BACKGROUND_URLS: 'Background image URLs must be an object',
  INVALID_LEFT_BACKGROUND_URL: 'Left background image URL is required and must be a string',
  INVALID_MID_BACKGROUND_URL: 'Middle background image URL is required and must be a string',
  INVALID_RIGHT_BACKGROUND_URL: 'Right background image URL is required and must be a string',
  INVALID_URL_FORMAT: 'Invalid URL format',
  INVALID_FONT_URL: 'Font URL is required and must be a string',
  INVALID_PADDING_STRING: 'Padding must be a string with one or two non-negative numbers',
  INVALID_TEXT: 'Text is required and cannot be empty',
  INVALID_TEXT_PROPS: 'Text properties must be an object',
  INVALID_FONT_COLOR: 'Font color is required and must be a string',
  INVALID_FONT_SIZE: 'Font size must be a positive number',
  INVALID_TRADEMARK_GAP: 'Text-trademark gap must be a non-negative number',
  INVALID_TRADEMARK_URL: 'Trademark image URL is required and must be a string',
  INVALID_CUTLINE_MARGIN: 'Cutline margin must be an object with non-negative numbers',
  INVALID_CUTLINE_RECT_ROUND: 'Cutline rect round must be a non-negative number',
  INVALID_RING_CONNECTION_ROUND: 'Ring connection round must be a non-negative number',
};

export const SVG_DEFAULTS = {
  COLOR: '#FF69B4',
  STROKE_WIDTH: '1',
  SVG_NAMESPACE: 'http://www.w3.org/2000/svg',
};

export const CHARM_DEFAULTS = {
  BACKGROUND: {
    IMAGE_URLS: {
      LEFT: 'https://s3.ap-northeast-2.amazonaws.com/s3.marpple.co/ness/charm/left.png',
      MID: 'https://s3.ap-northeast-2.amazonaws.com/s3.marpple.co/ness/charm/mid.png',
      RIGHT: 'https://s3.ap-northeast-2.amazonaws.com/s3.marpple.co/ness/charm/right.png',
    },
  },
  FONT: {
    FAMILY: 'https://s3.marpple.co/font/maker/ko/morris__normal__normal.woff',
    COLOR: '#FFFFFF',
    SIZE: 63, // px
    PADDING: '15 0',
  },
  TRADEMARK: {
    IMAGE_URL: 'https://s3.ap-northeast-2.amazonaws.com/s3.marpple.co/ness/charm/tradmark-nexon-600dpi.png',
    GAP_PX: 10,
  },
  CUTLINE: {
    MARGIN: {
      TOP: 20,
      BOTTOM: 20,
      LEFT: 20,
      RIGHT: 20,
    },
    ROUND: 24,
    RING_CONNECTION_ROUND_PX: 12,
  },
};
